import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { useLazyListMembershipsQuery } from 'memberships/reduxQueries'
import StatusLabel from './statusLabel'
import { Membership } from 'memberships/schema'
import { TableColumn } from 'uiComponents/table/reactTable/interface'
import { DateFormats } from 'utils/dates'
import { formatISOString } from 'utils'
import { useParams, useLocation } from 'react-router-dom'
import { BaseRouteParams, useNavigation } from 'hocs'
import MembershipsTableFilters from './membershipsTableFilter'
import { getFiltersFromUrl } from 'uiComponents/filter/filterHelpers'
import { InfiniteTable } from 'uiComponents/table/reactTable/infiniteTable'
import RenewEnabledIcon from 'images/renew-enabled.svg'
import RenewDisabledIcon from 'images/renew-disabled.svg'
import classNames from 'classnames'
import SearchField from 'uiComponents/search/searchField'
import { SearchScopeBadge } from 'uiComponents/searchScopeBadge'
import { useTranslation } from 'translations'
import './membershipsTable.scss'

interface MembershipsTableProps {
    onSelectMembership?: (membershipId: string) => void
}

const MembershipsTable: React.FC<MembershipsTableProps> = ({ onSelectMembership }) => {
    const { t } = useTranslation()
    const { accountSlug } = useParams<BaseRouteParams>()
    const navigation = useNavigation()
    const location = useLocation()
    const [listMemberships, { isFetching, data, error }] = useLazyListMembershipsQuery()
    const [statusFilter, setStatusFilter] = useState<string>(getFiltersFromUrl('membership_status'))
    const [search, setSearch] = useState<string>(navigation.query().q)
    const [allData, setAllData] = useState<Membership[]>([])

    const loadData = () => {
        setAllData([])
        listMemberships({
            accountSlug,
            status: statusFilter,
            pageSize: 10,
            search,
        })
    }

    const loadMore = useCallback(() => {
        if (data?.next && !error) {
            listMemberships({
                accountSlug,
                url: data?.next,
            })
        }
    }, [accountSlug, data?.next, listMemberships, statusFilter, search, error])

    useEffect(loadData, [accountSlug, statusFilter, search])

    useEffect(() => {
        const newStatusFilter = getFiltersFromUrl('membership_status')
        if (newStatusFilter !== statusFilter) {
            setStatusFilter(newStatusFilter)
        }
    }, [location.search])

    useEffect(() => {
        const newSearch = navigation.query().q
        if (newSearch !== search) {
            setSearch(newSearch)
        }
    }, [navigation.query().q])

    useEffect(() => {
        if (data?.results) {
            setAllData((prev) => [...prev, ...data.results])
        }
    }, [data?.results])

    const handleRowClick = useCallback(
        (row: Membership) => {
            if (onSelectMembership) {
                onSelectMembership(row.uuid)
            }
        },
        [onSelectMembership],
    )

    const columns = useMemo<TableColumn<Membership>[]>(
        () => [
            {
                Header: t('memberships.membershipsTable.columns.status'),
                accessor: 'status',
                width: '150px',
                Cell: ({ value }) => <StatusLabel status={value} />,
            },
            {
                Header: t('memberships.membershipsTable.columns.membershipId'),
                accessor: 'membershipId',
                width: '140px',
                disableSortBy: true,
                Cell: ({ value }) => <span title={value}>{value}</span>,
            },
            {
                Header: t('memberships.membershipsTable.columns.email'),
                accessor: 'email',
                width: '240px',
                disableSortBy: true,
                Cell: ({ value }) => <span title={value}>{value}</span>,
            },
            {
                Header: t('memberships.membershipsTable.columns.product'),
                accessor: 'article.name',
                width: '240px',
                disableSortBy: true,
            },
            {
                Header: t('memberships.membershipsTable.columns.passHolder'),
                accessor: 'memberFullName',
                width: '240px',
                disableSortBy: true,
                Cell: ({
                    row: {
                        original: { member },
                    },
                }) => {
                    const fullName = `${member?.firstName || ''} ${member?.lastName || ''}`.trim()
                    return <span title={fullName}>{fullName}</span>
                },
            },
            {
                Header: t('memberships.membershipsTable.columns.expiresAt'),
                accessor: 'expiresAt',
                width: '190px',
                disableSortBy: true,
                Cell: ({ value, row: { original } }) => (
                    <span>
                        {formatISOString(value, DateFormats.EXTRA_LONG_DATE)}
                        <div
                            className={classNames('renewal-icon', {
                                active: original.autoRenewEnabled,
                            })}
                            title={
                                original.autoRenewEnabled
                                    ? t('memberships.membershipsTable.willRenew')
                                    : t('memberships.membershipsTable.willNotRenew')
                            }
                        >
                            {original.autoRenewEnabled ? (
                                <RenewEnabledIcon alt={t('memberships.membershipsTable.autoRenewEnabled')} />
                            ) : (
                                <RenewDisabledIcon alt={t('memberships.membershipsTable.autoRenewDisabled')} />
                            )}
                        </div>
                    </span>
                ),
            },
        ],
        [t],
    )

    const noResultsRow = error
        ? {
              headline: t('memberships.membershipsTable.loadError'),
              text: t('memberships.membershipsTable.loadErrorHint'),
          }
        : isFetching
        ? {
              headline: t('memberships.membershipsTable.loading'),
              text: t('memberships.membershipsTable.loadingHint'),
          }
        : {
              headline: t('memberships.membershipsTable.emptyState'),
              text: t('memberships.membershipsTable.emptyStateHint'),
          }

    return (
        <div className="memberships-table-container">
            <MembershipsTableFilters />
            <SearchField className="search" placeholder={t('memberships.membershipsTable.searchPlaceholder')} />
            {/* The Overview/Pricing tab is a page-level view, not search scope. */}
            <SearchScopeBadge keepParams={['tab']} />
            <InfiniteTable
                data={allData}
                columns={columns}
                loadingMore={isFetching}
                pagination={{
                    next: data?.next,
                    previous: data?.previous,
                }}
                onLoadMore={loadMore}
                noResultsRow={noResultsRow}
                rowProps={(row: any) => ({
                    onClick: () => handleRowClick(row.original as Membership),
                    className: 'clickable-row',
                })}
            />
        </div>
    )
}

export default MembershipsTable
